import { OverlayRect } from './boundingBoxOverlay';

export interface PdfTextItem {
  str: string;
  transform: number[];
  width: number;
  height: number;
}

export interface ViewportPoints {
  width: number;
  height: number;
  convertToViewportPoint: (x: number, y: number) => number[];
}

interface CharMap {
  item: PdfTextItem;
  start: number;
  end: number;
}

export function locateValueOverlay(
  items: PdfTextItem[],
  value: string | undefined,
  viewport: ViewportPoints
): OverlayRect | null {
  const matched = findMatchingSpans(items, value);
  if (matched.length === 0) {
    return null;
  }

  return unionToOverlay(matched, viewport);
}

export function findMatchingItems(items: PdfTextItem[], value: string | undefined): PdfTextItem[] {
  const seen = new Set<PdfTextItem>();
  const matched: PdfTextItem[] = [];
  findMatchingSpans(items, value).forEach((span) => {
    if (!seen.has(span.item)) {
      seen.add(span.item);
      matched.push(span.item);
    }
  });
  return matched;
}

function findMatchingSpans(items: PdfTextItem[], value: string | undefined): CharMap[] {
  const needle = normalize(value);
  const gluedNeedle = glue(needle);
  if (!needle) {
    return [];
  }

  const tokens = items.filter((item) => normalize(item.str));
  return findSequence(tokens, needle, false) ?? findSequence(tokens, gluedNeedle, true) ?? [];
}

function findSequence(tokens: PdfTextItem[], needle: string, glued: boolean): CharMap[] | null {
  let haystack = '';
  const map: Array<CharMap | null> = [];

  tokens.forEach((item) => {
    const raw = item.str ?? '';
    const length = Math.max(raw.length, 1);
    let emitted = false;
    let pendingSpace = false;

    for (let index = 0; index < raw.length; index += 1) {
      const ch = raw[index];
      if (!/[a-z0-9]/i.test(ch)) {
        pendingSpace = emitted;
        continue;
      }

      if (!glued && haystack.length > 0 && (pendingSpace || !emitted)) {
        haystack += ' ';
        map.push(null);
      }

      haystack += ch.toLowerCase();
      map.push({
        item,
        start: index / length,
        end: (index + 1) / length,
      });
      emitted = true;
      pendingSpace = false;
    }
  });

  const start = haystack.indexOf(needle);
  if (start < 0) {
    return null;
  }

  const end = start + needle.length;
  const spans = new Map<PdfTextItem, CharMap>();
  for (let i = start; i < end && i < map.length; i += 1) {
    const entry = map[i];
    if (!entry) {
      continue;
    }
    const current = spans.get(entry.item);
    if (!current) {
      spans.set(entry.item, { item: entry.item, start: entry.start, end: entry.end });
    } else {
      current.start = Math.min(current.start, entry.start);
      current.end = Math.max(current.end, entry.end);
    }
  }

  return spans.size > 0 ? Array.from(spans.values()) : null;
}

function unionToOverlay(spans: CharMap[], viewport: ViewportPoints): OverlayRect | null {
  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;

  spans.forEach((span) => {
    const item = span.item;
    const x = (item.transform[4] ?? 0) + item.width * span.start;
    const y = item.transform[5] ?? 0;
    const width = item.width * Math.max(0.02, span.end - span.start);
    const corners = [
      viewport.convertToViewportPoint(x, y),
      viewport.convertToViewportPoint(x + width, y),
      viewport.convertToViewportPoint(x, y + item.height),
      viewport.convertToViewportPoint(x + width, y + item.height),
    ];
    corners.forEach(([vx, vy]) => {
      minX = Math.min(minX, vx);
      minY = Math.min(minY, vy);
      maxX = Math.max(maxX, vx);
      maxY = Math.max(maxY, vy);
    });
  });

  if (!Number.isFinite(minX) || viewport.width <= 0 || viewport.height <= 0) {
    return null;
  }

  const padX = 0.4;
  const padY = 0.5;
  const left = (minX / viewport.width) * 100 - padX;
  const top = (minY / viewport.height) * 100 - padY;
  const width = ((maxX - minX) / viewport.width) * 100 + padX * 2;
  const height = Math.max(1.35, ((maxY - minY) / viewport.height) * 100 + padY * 2);

  if (width <= 0 || height <= 0) {
    return null;
  }

  return {
    left: clampPercent(left),
    top: clampPercent(top),
    width: Math.min(100 - clampPercent(left), width),
    height: Math.min(100 - clampPercent(top), height),
  };
}

function normalize(value: string | undefined): string {
  return (value ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');
}

function glue(value: string): string {
  return value.replace(/\s+/g, '');
}

function clampPercent(value: number): number {
  if (Number.isNaN(value)) {
    return 0;
  }
  return Math.min(100, Math.max(0, value));
}
